<template>
    <ds-modal
        class="custom-domain-settings-modal"
        :is-open="isOpen"
        show-close
        title="Add a domain"
        @close="close"
    >
        <span class="title">
            {{ $options.MESSAGE_CONSTANTS.stepOneTitle }}
        </span>
        <p class="description">
            {{ $options.MESSAGE_CONSTANTS.stepOneDescription }}
        </p>

        <ds-input-field
            v-model="selectedDomainName"
            data-qa="selectedDomainNameQa"
            name="domain"
            :label="domainLabel"
            :required="isNewDomain"
            :submitted="domainSubmitted"
            :invalid="invalidNewDomain"
            @blur="handleDomainBlur"
        >
            <template #error>
                {{ $options.MESSAGE_CONSTANTS.domainError }}
            </template>
        </ds-input-field>

        <ds-select-field
            v-model="selectedProvider"
            name="providers"
            :label="providerLabel"
            class="host-provider"
            label-prop="name"
            value-prop="id"
            bind-value-only
            required
            :options="providers"
        />
        <span class="title">
            {{ $options.MESSAGE_CONSTANTS.stepTwoTitle }}
        </span>

        <p class="description">
            {{ $options.MESSAGE_CONSTANTS.stepTwoDescription }}
        </p>
        <span class="description-link">
            {{ $options.MESSAGE_CONSTANTS.stepTwoDescriptionLink }}
        </span>

        <ol>
            <li>{{ $options.MESSAGE_CONSTANTS.stepOne }}</li>
            <li>{{ $options.MESSAGE_CONSTANTS.stepTwo }}</li>
            <div data-id="copy-host-field-button-qa" @click="copy(hostField)">
                <ds-input-field v-model="hostField">
                    <template #trailing>
                        <ds-icon class="copy-icon" name="copy" />
                    </template>
                </ds-input-field>
            </div>
            <li>{{ $options.MESSAGE_CONSTANTS.stepThree }}</li>
            <div data-id="copy-points-to-field-button-qa" class="copy-input" @click="copy(pointsTo)">
                {{ pointsTo }}

                <ds-icon class="copy-icon" name="copy" />
            </div>
        </ol>

        <div class="button-row">
            <ds-filled-button
                data-qa="close-custom-domain-button-qa"
                class="button-close"
                @click="close"
            >
                {{ $options.MESSAGE_CONSTANTS.close }}
            </ds-filled-button>

            <ds-spinner v-if="isSaving" data-qa="loading-spinner" />

            <ds-filled-button
                v-else
                data-qa="verify-custom-domain-button-qa"
                class="verify-button"
                @click="submitForm"
            >
                {{ $options.MESSAGE_CONSTANTS.verify }}
            </ds-filled-button>
        </div>
    </ds-modal>
</template>

<script>

export const MESSAGE_CONSTANTS = {
    stepOneTitle: 'Step 1',
    stepOneDescription: 'Enter your domain (subdomain.domain.com) and host provider. A sub-domain is required.',
    stepTwoTitle: 'Step 2',
    stepTwoDescription: 'Create a new CNAME record.',
    stepTwoDescriptionLink: 'We suggest contacting your host for assistance in locating your CNAME records',
    stepOne: 'Create a new CNAME record in provider',
    stepTwo: 'Copy paste text into "Name" or "Host" field',
    stepThree: 'Copy paste text into "Value" or "Points to" field',
    close: 'Close',
    verify: 'Verify',
    domainError: "Custom domains must be entered in the form 'subdomain.domain.com'",
    domainNameLabel: 'Domain URL',
    domainProviderLabel: 'Domain host/provider',
};

import {
    DKIM_PROVIDER_GODADDY_ID,
    DKIM_PROVIDER_BLUEHOST_ID,
    DKIM_PROVIDER_HOSTGATOR_ID,
    DKIM_PROVIDER_DREAMHOST_ID,
    DKIM_PROVIDER_LIQUIDWEB_ID,
    DKIM_PROVIDER_INMOTION_ID,
    DKIM_PROVIDER_OTHER_ID,
} from '../shared/constants/domain.settings.constants';
import { customDomainPointsTo } from '../util/marketing/site.utils';

export const CUSTOM_DOMAIN_REGEX = '\\b(?:[a-zA-Z0-9](?:[a-zA-Z0-9\\-]{0,61}[a-zA-Z0-9])?\\.){2}(?:[a-zA-Z]{2,63}(?:\\.[a-zA-Z]{2,63})*)\\b';
export const HTTP_PREFIX = 'http://';
export const HTTPS_PREFIX = 'https://';
export const WWW_PREFIX = 'www.';

export default {
    MESSAGE_CONSTANTS,
    props: {
        isOpen: {
            type: Boolean,
            default: false,
        },
        isNewDomain: {
            type: Boolean,
            default: true,
        },
        domain: {
            type: Object,
            required: true,
        },
        currentEnv: {
            type: String,
            required: true,
        },
        isSaving: Boolean,
    },
    data() {
        return {
            selectedDomainName: this.isNewDomain ? '' : this.domain.domainName,
            selectedProvider: null,
            providers: [
                { id: DKIM_PROVIDER_GODADDY_ID, name: 'GoDaddy', url: 'https://www.godaddy.com/help/add-a-cname-record-19236' },
                { id: DKIM_PROVIDER_BLUEHOST_ID, name: 'BlueHost', url: 'https://my.bluehost.com/hosting/help/559/#add' },
                { id: DKIM_PROVIDER_HOSTGATOR_ID, name: 'Host Gator', url: 'https://support.hostgator.com/articles/articles/hosting-guide/lets-get-started/dns-name-servers/manage-dns-records-with-hostgatorenom' },
                { id: DKIM_PROVIDER_DREAMHOST_ID, name: 'DreamHost', url: 'https://help.dreamhost.com/hc/en-us/articles/215414867-How-do-I-add-custom-DNS-records-/#CNAME_record' },
                { id: DKIM_PROVIDER_LIQUIDWEB_ID, name: 'Liquid Web', url: 'https://www.liquidweb.com/kb/how-to-add-or-modify-dns-records-in-manage/#addRecord' },
                { id: DKIM_PROVIDER_INMOTION_ID, name: 'In-Motion', url: 'https://www.inmotionhosting.com/support/domain-names/dns-nameserver-changes/create-cname-record' },
                { id: DKIM_PROVIDER_OTHER_ID, name: 'Other', url: 'Show our help link https://learn.thryv.com/hc/en-us/articles/37241726237069-DKIM' },
            ],
            isSubmitted: false,
            invalidNewDomain: false,
        };
    },
    watch: {
        selectedDomainName: {
            handler(newValue) {
                let result = newValue.toLowerCase();

                // Any white space replaced by -:
                result = result.replace(/\s/, '-');
                // No more than one '-' sequence:
                result = result.replace(/[-]{2,}/, '-');
                // No more than one ':' sequence:
                result = result.replace(/[:]{2,}/, ':');
                // No more than one '.' sequence:
                result = result.replace(/[.]{2,}/, '.');
                // No more than two '/' sequence:
                result = result.replace(/[/]{3,}/, '//');
                // Only words or '.' ':' '/' '-'
                result = result.replace(/[^\w:/.-]/gi, '');

                this.$nextTick(() => {
                    this.selectedDomainName = result;
                    this.invalidNewDomain = false;
                    this.isSubmitted = false;
                });
            },
        },
    },
    computed: {
        domainSubmitted() {
            return (this.isSubmitted && this.isNewDomain) || this.invalidNewDomain;
        },
        domainLabel() {
            return MESSAGE_CONSTANTS.domainNameLabel;
        },
        providerLabel() {
            return MESSAGE_CONSTANTS.domainProviderLabel;
        },
        pointsTo() {
            return customDomainPointsTo(this.currentEnv);
        },
        hostField() {
            return this.selectedDomainName?.split('.')[0] ?? '';
        },
    },
    methods: {
        sanitizedDomain(domain) {
            const matches = this.removeUriPrefix(domain).match(CUSTOM_DOMAIN_REGEX);

            return (matches?.length > 0) ? matches[0] : '';
        },
        removeUriPrefix(domain) {
            domain = (domain.startsWith(HTTPS_PREFIX)) ? domain.replace(HTTPS_PREFIX, '') : domain;
            domain = (domain.startsWith(HTTP_PREFIX)) ? domain.replace(HTTP_PREFIX, '') : domain;
            domain = (domain.startsWith(WWW_PREFIX)) ? domain.replace(WWW_PREFIX, '') : domain;

            return domain;
        },
        close() {
            this.$emit('close');
        },
        submitForm() {
            const data = {
                domain: this.selectedDomainName,
            };

            this.$emit('submit-form', data);
        },
        copy(value) {
            if (value) {
                this.$emit('copy', value);
            }
        },
        handleDomainBlur() {
            if (this.isNewDomain) {
                this.sanitizedDomainName = this.sanitizedDomain(this.selectedDomainName);

                if (this.sanitizedDomainName) {
                    this.invalidNewDomain = false;
                    this.selectedDomainName = this.sanitizedDomainName;
                } else {
                    this.invalidNewDomain = true;
                }
            }
        },
    },
};
</script>

<style lang="scss" rel="stylesheet/scss" type="text/scss" scoped>
.copy-input {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background-color: $color-paper;
  border: 1px solid $color-gray-400;
  padding: $spacing-100 $spacing-200;
  border-radius: $spacing-100;
  margin: 0 0 10px 0;
  width: 100%;
}

.copy-input:active {
  border-color: blue;
}

.copy-input:hover {
  cursor: pointer;
}

span .title {
   font-weight: bold;
    margin-bottom: 0px;
}

p {
  margin: 0 0 4px 0;
}

.description {
  margin-top: 1rem;
}

.description-link {
  margin-bottom: 1rem;
  font-size: small;
}

.verify-button {
    color: white;
}

ol li {
  margin-bottom: 10px;
}

.button-close {
    background-color: transparent;
    color: #0042db;
    font-family: unset;
}

.copy-icon {
    color: #0042db;
}

</style>
<style lang="scss">
div.host-provider > div > div.dropdown-reference > div.select-container > label {
    padding-top: 0px;
}

div.host-provider > div.dropdown >  .dropdown-menu.open {
    width: 100%;
}

div.custom-domain-settings-modal div.modal-body-inner {
  background-color: white;
}

div.custom-domain-settings-modal .ds-modal-header-title {
  font-weight: bold;
}

#vue-content-custom-domain-mgmt > div > section:nth-child(1) > div > div > div > div.modal-header{
    background-color: white;
}
</style>
